/**
 * An employee's employment contracts — list and create, WP-W6.
 * `web/shapes/hr-documents.md` §6 is the agreed shape.
 *
 * A contract is a **record, not a quantity**, so there is no chart: a list of the
 * employee's contracts (most recent first, a bare `{ data }` read whole), and a create
 * form. `contract_type` and `start_date` are the server's `required` fields, so the
 * submit is disabled until both are set — a true sentence about the operator, not about
 * permission, and the shape that keeps a `400` from being how a mandatory field is
 * learned. Every other field is optional and **omitted when blank** rather than sent
 * empty (`exactOptionalPropertyTypes` makes those two different requests).
 *
 * `base_salary` goes out as a **number** (the wire's `type: number` on the create) and
 * comes back as an exact decimal string on the row — a difference the contract has, so
 * the form parses the field to a number on submit and renders the string verbatim.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type {
  HrContract,
  HrContractCreateRequest,
  HrContractStatus,
  HrContractType,
} from '../../api/hr';
import { HR_CONTRACT_STATUSES, HR_CONTRACT_TYPES } from '../../api/hr';
import { HR_CONTRACT_STATUS_LABEL, HR_CONTRACT_TYPE_LABEL } from '../../hr/employee-document';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON, type Severity } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

const CONTRACT_STATUS_BADGE: Readonly<Record<HrContractStatus, Severity>> = {
  draft: 'info',
  active: 'low',
  expired: 'medium',
  terminated: 'high',
};

export interface ContractsPanelProps {
  readonly status: QueryStatus;
  readonly contracts: readonly HrContract[] | null;
  readonly readError: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly locale: string;
  readonly timezone: string;
  readonly createPending: boolean;
  readonly createError: WorkforceApiError | null;
  readonly created: HrContract | null;
  readonly onCreate: (body: HrContractCreateRequest) => void;
}

export function ContractsPanel({
  status,
  contracts,
  readError,
  onReload,
  locale,
  timezone,
  createPending,
  createError,
  created,
  onCreate,
}: ContractsPanelProps): ReactNode {
  const { t } = useI18n();
  const [contractType, setContractType] = useState<'' | HrContractType>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [baseSalary, setBaseSalary] = useState('');
  const [currency, setCurrency] = useState('');
  const [contractStatus, setContractStatus] = useState<'' | HrContractStatus>('');

  const ready = contractType !== '' && startDate.trim() !== '';

  const build = (): HrContractCreateRequest => {
    const end = endDate.trim();
    const salary = baseSalary.trim();
    const currency_ = currency.trim();
    return {
      // `ready` guards this; `contractType` is non-empty here.
      contract_type: contractType as HrContractType,
      start_date: startDate,
      ...(end === '' ? {} : { end_date: end }),
      ...(salary === '' ? {} : { base_salary: Number(salary) }),
      ...(currency_ === '' ? {} : { currency: currency_ }),
      ...(contractStatus === '' ? {} : { status: contractStatus }),
    };
  };

  return (
    <section
      className="wp-section"
      aria-labelledby="w6d-contracts-heading"
      data-testid="w6d-contracts"
    >
      <h3 className="wp-section__title" id="w6d-contracts-heading">
        {t('hr.contracts.title')}
      </h3>
      <p className="wp-small wp-muted">{t('hr.contracts.intro')}</p>

      {/* ── Create ─────────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w6d-contracts-create">
        <h4 className="wp-section__title">{t('hr.contracts.create.title')}</h4>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!ready || createPending) return;
            onCreate(build());
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6d-contract-type">
              {t('hr.contracts.create.type')}
            </label>
            <select
              className="wp-field__input"
              id="w6d-contract-type"
              data-testid="w6d-contract-type"
              value={contractType}
              onChange={(event) => {
                setContractType(event.target.value as '' | HrContractType);
              }}
            >
              <option value="">{t('hr.contracts.type.none')}</option>
              {HR_CONTRACT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(HR_CONTRACT_TYPE_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <DateField
            className="wp-filters__group"
            id="w6d-contract-start"
            testId="w6d-contract-start"
            label={t('hr.contracts.create.startDate')}
            value={startDate}
            onChange={setStartDate}
          />
          <DateField
            className="wp-filters__group"
            id="w6d-contract-end"
            testId="w6d-contract-end"
            label={t('hr.contracts.create.endDate')}
            value={endDate}
            onChange={setEndDate}
          />
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6d-contract-salary">
              {t('hr.contracts.create.baseSalary')}
            </label>
            <input
              className="wp-field__input"
              id="w6d-contract-salary"
              data-testid="w6d-contract-salary"
              type="number"
              min="0"
              value={baseSalary}
              autoComplete="off"
              onChange={(event) => {
                setBaseSalary(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6d-contract-currency">
              {t('hr.contracts.create.currency')}
            </label>
            <input
              className="wp-field__input"
              id="w6d-contract-currency"
              data-testid="w6d-contract-currency"
              type="text"
              value={currency}
              maxLength={10}
              autoComplete="off"
              onChange={(event) => {
                setCurrency(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6d-contract-status">
              {t('hr.contracts.create.status')}
            </label>
            <select
              className="wp-field__input"
              id="w6d-contract-status"
              data-testid="w6d-contract-status"
              value={contractStatus}
              onChange={(event) => {
                setContractStatus(event.target.value as '' | HrContractStatus);
              }}
            >
              <option value="">{t('hr.contracts.status.none')}</option>
              {HR_CONTRACT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(HR_CONTRACT_STATUS_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w6d-contract-submit"
              disabled={!ready || createPending}
            >
              {createPending
                ? t('hr.contracts.create.submitting')
                : t('hr.contracts.create.submit')}
            </button>
          </div>
        </form>
        {createError === null ? null : (
          <ApiErrorNote
            error={createError}
            subject={t('hr.contracts.subject')}
            testId="w6d-contract-create-error"
          />
        )}
        {created === null ? null : (
          <StatusNote
            severity="low"
            title={t('hr.contracts.create.ok', {
              type:
                created.contract_type === null
                  ? t('hr.contracts.type.none')
                  : t(HR_CONTRACT_TYPE_LABEL[created.contract_type]),
            })}
            testId="w6d-contract-create-ok"
          />
        )}
      </div>

      {/* ── The list ──────────────────────────────────────────────────────── */}
      {status === 'loading' ? (
        <Skeleton
          rows={3}
          variant="block"
          label={t('hr.contracts.subject')}
          testId="w6d-contracts-skeleton"
        />
      ) : null}
      {status === 'error' && readError !== null ? (
        <ApiErrorNote
          error={readError}
          onRetry={onReload}
          subject={t('hr.contracts.subject')}
          testId="w6d-contracts-error"
        />
      ) : null}

      {contracts === null ? null : contracts.length === 0 ? (
        <EmptyState
          title={t('hr.contracts.empty.title')}
          description={t('hr.contracts.empty.body')}
          testId="w6d-contracts-empty"
        />
      ) : (
        <ul className="wp-rows" data-testid="w6d-contracts-list">
          {contracts.map((contract) => {
            const badge =
              contract.status === null ? 'info' : CONTRACT_STATUS_BADGE[contract.status];
            return (
              <li className="wp-row" key={contract.id} data-testid={`w6d-contract-${contract.id}`}>
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity={badge}
                      icon={SEVERITY_ICON[badge]}
                      label={
                        contract.status === null
                          ? t('hr.contracts.status.none')
                          : t(HR_CONTRACT_STATUS_LABEL[contract.status])
                      }
                      testId={`w6d-contract-status-${contract.id}`}
                    />{' '}
                    <span>
                      {contract.contract_type === null
                        ? t('hr.contracts.type.none')
                        : t(HR_CONTRACT_TYPE_LABEL[contract.contract_type])}
                    </span>
                  </p>
                  <p className="wp-small wp-muted">
                    {t('hr.contracts.row.meta', {
                      start: contract.start_date,
                      end: contract.end_date ?? t('hr.contracts.row.noEnd'),
                      salary:
                        contract.base_salary === null
                          ? t('hr.contracts.row.noSalary')
                          : t('hr.contracts.row.salary', {
                              amount: contract.base_salary,
                              currency: contract.currency ?? '',
                            }),
                    })}
                  </p>
                  <p
                    className="wp-small wp-muted"
                    data-testid={`w6d-contract-updated-${contract.id}`}
                  >
                    {t('hr.contracts.lastUpdated', {
                      when: formatInstant(contract.updated_at, locale, timezone),
                    })}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
